import {
  getChannelDriveCopy,
  type ChannelDriveCopy,
} from './channel-drive-copy';
import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import {
  getDriveLocalizedLabels,
  type DriveLocalizedLabels,
} from '@/i18n/drives-labels-copy';
import {
  getDriveBreakGlassCopy,
  type DriveBreakGlassCopy,
} from '@/i18n/drives-break-glass-copy';
import {
  getDriveManagementCopy,
  type DriveManagementCopy,
} from '@/i18n/drives-management-copy';

export type DrivesCopy = ChannelDriveCopy &
  DriveLocalizedLabels &
  DriveBreakGlassCopy &
  DriveManagementCopy & {
    drives: string;
    driveNoun: string;
    myDrive: string;
    workspaceLibrary: string;
    workspaceLibraryCallout: string;
    sharedWithMe: string;
    teams: string;
    projects: string;
    /** The every-drive view; the Files rail's overflow row uses the same words. */
    allDrives: string;
    pinnedDrives: string;
    pinDrive: string;
    unpinDrive: string;
  };

const NAV_COPY: Record<
  SupportedLocale,
  Pick<
    DrivesCopy,
    | 'drives'
    | 'driveNoun'
    | 'myDrive'
    | 'workspaceLibrary'
    | 'workspaceLibraryCallout'
    | 'sharedWithMe'
    | 'teams'
    | 'projects'
    | 'allDrives'
    | 'pinnedDrives'
    | 'pinDrive'
    | 'unpinDrive'
  >
> = {
  en: {
    drives: 'Drives',
    driveNoun: 'Drive',
    myDrive: 'My Drive',
    workspaceLibrary: 'Workspace Drive',
    workspaceLibraryCallout:
      'IMAI uses this drive by default for products, references, and files shared across the workspace.',
    sharedWithMe: 'Shared with me',
    teams: 'Teams',
    projects: 'Projects',
    allDrives: 'All drives',
    pinnedDrives: 'Pinned Drives',
    pinDrive: 'Pin Drive',
    unpinDrive: 'Unpin Drive',
  },
  'pt-BR': {
    drives: 'Unidades',
    driveNoun: 'Unidade',
    myDrive: 'Meu Drive',
    workspaceLibrary: 'Unidade do espaço de trabalho',
    workspaceLibraryCallout:
      'O IMAI usa essa unidade por padr\u00E3o para produtos, refer\u00EAncias e arquivos compartilhados pelo espa\u00E7o de trabalho.',
    sharedWithMe: 'Compartilhados comigo',
    teams: 'Equipes',
    projects: 'Projetos',
    allDrives: 'Todos os drives',
    pinnedDrives: 'Unidades fixadas',
    pinDrive: 'Fixar unidade',
    unpinDrive: 'Desafixar unidade',
  },
  ar: {
    drives: 'محركات الأقراص',
    driveNoun: 'محرك الأقراص',
    myDrive: 'ملفاتي',
    workspaceLibrary: 'محرك مساحة العمل',
    workspaceLibraryCallout:
      'يستخدم IMAI محرك الأقراص هذا افتراضيًا للمنتجات والمراجع والملفات المشتركة في مساحة العمل.',
    sharedWithMe: 'تمت مشاركتها معي',
    teams: 'الفرق',
    projects: 'المشاريع',
    allDrives: 'كل محركات الأقراص',
    pinnedDrives: 'محركات أقراص مثبتة',
    pinDrive: 'تثبيت محرك الأقراص',
    unpinDrive: 'إلغاء تثبيت محرك الأقراص',
  },
  de: {
    drives: 'Ablagen',
    driveNoun: 'Ablage',
    myDrive: 'Meine Ablage',
    workspaceLibrary: 'Workspace-Ablage',
    workspaceLibraryCallout:
      'IMAI nutzt diese Ablage standardmäßig für Produkte, Referenzen und Dateien, die im Workspace geteilt werden.',
    sharedWithMe: 'Für mich freigegeben',
    teams: 'Teams',
    projects: 'Projekte',
    allDrives: 'Alle Drives',
    pinnedDrives: 'Angeheftete Ablagen',
    pinDrive: 'Ablage anheften',
    unpinDrive: 'Ablage lösen',
  },
  es: {
    drives: 'Unidades',
    driveNoun: 'Unidad',
    myDrive: 'Mi unidad',
    workspaceLibrary: 'Unidad del espacio',
    workspaceLibraryCallout:
      'IMAI usa esta unidad de forma predeterminada para productos, referencias y archivos compartidos en todo el espacio de trabajo.',
    sharedWithMe: 'Compartido conmigo',
    teams: 'Equipos',
    projects: 'Proyectos',
    allDrives: 'Todas las unidades',
    pinnedDrives: 'Unidades fijadas',
    pinDrive: 'Fijar unidad',
    unpinDrive: 'Desfijar unidad',
  },
  fr: {
    drives: 'Espaces de stockage',
    driveNoun: 'Espace',
    myDrive: 'Mon espace',
    workspaceLibrary: 'Espace du workspace',
    workspaceLibraryCallout:
      'IMAI utilise cet espace par défaut pour les produits, références et fichiers partagés dans tout le workspace.',
    sharedWithMe: 'Partagés avec moi',
    teams: 'Équipes',
    projects: 'Projets',
    allDrives: 'Tous les drives',
    pinnedDrives: 'Espaces épinglés',
    pinDrive: 'Épingler l’espace',
    unpinDrive: 'Désépingler l’espace',
  },
  hi: {
    drives: 'ड्राइव',
    driveNoun: 'ड्राइव',
    myDrive: 'मेरी ड्राइव',
    workspaceLibrary: 'वर्कस्पेस ड्राइव',
    workspaceLibraryCallout:
      'IMAI इस ड्राइव का इस्तेमाल डिफ़ॉल्ट रूप से पूरे वर्कस्पेस में साझा किए गए उत्पादों, संदर्भों और फ़ाइलों के लिए करता है।',
    sharedWithMe: 'मेरे साथ साझा',
    teams: 'टीमें',
    projects: 'प्रोजेक्ट',
    allDrives: 'सभी ड्राइव',
    pinnedDrives: 'पिन की गई ड्राइव',
    pinDrive: 'ड्राइव पिन करें',
    unpinDrive: 'ड्राइव अनपिन करें',
  },
  id: {
    drives: 'Drive',
    driveNoun: 'Drive',
    myDrive: 'Drive Saya',
    workspaceLibrary: 'Drive Workspace',
    workspaceLibraryCallout:
      'IMAI memakai drive ini secara default untuk produk, referensi, dan file yang dibagikan di seluruh workspace.',
    sharedWithMe: 'Dibagikan kepada saya',
    teams: 'Tim',
    projects: 'Proyek',
    allDrives: 'Semua drive',
    pinnedDrives: 'Drive Tersemat',
    pinDrive: 'Sematkan Drive',
    unpinDrive: 'Lepas sematan Drive',
  },
  it: {
    drives: 'Drive',
    driveNoun: 'Drive',
    myDrive: 'Il mio Drive',
    workspaceLibrary: 'Drive dello spazio',
    workspaceLibraryCallout:
      'IMAI usa questo Drive come posizione predefinita per prodotti, riferimenti e file condivisi in tutto lo spazio di lavoro.',
    sharedWithMe: 'Condivisi con me',
    teams: 'Team',
    projects: 'Progetti',
    allDrives: 'Tutti i drive',
    pinnedDrives: 'Drive fissati',
    pinDrive: 'Fissa Drive',
    unpinDrive: 'Rimuovi Drive',
  },
  ja: {
    drives: 'ドライブ',
    driveNoun: 'ドライブ',
    myDrive: 'マイドライブ',
    workspaceLibrary: 'ワークスペース ドライブ',
    workspaceLibraryCallout:
      'IMAI は、ワークスペース全体で共有する製品、参照資料、ファイルの既定の保存先としてこのドライブを使用します。',
    sharedWithMe: '共有アイテム',
    teams: 'チーム',
    projects: 'プロジェクト',
    allDrives: 'すべてのドライブ',
    pinnedDrives: '固定したドライブ',
    pinDrive: 'ドライブを固定',
    unpinDrive: 'ドライブの固定を解除',
  },
  ko: {
    drives: '드라이브',
    driveNoun: '드라이브',
    myDrive: '내 드라이브',
    workspaceLibrary: '워크스페이스 드라이브',
    workspaceLibraryCallout:
      'IMAI는 워크스페이스 전체에서 공유하는 제품, 참조 자료, 파일의 기본 위치로 이 드라이브를 사용합니다.',
    sharedWithMe: '공유 문서함',
    teams: '팀',
    projects: '프로젝트',
    allDrives: '모든 드라이브',
    pinnedDrives: '고정된 드라이브',
    pinDrive: '드라이브 고정',
    unpinDrive: '드라이브 고정 해제',
  },
  ru: {
    drives: 'Диски',
    driveNoun: 'Диск',
    myDrive: 'Мой диск',
    workspaceLibrary: 'Диск пространства',
    workspaceLibraryCallout:
      'IMAI использует этот диск по умолчанию для продуктов, референсов и файлов, общих для всего рабочего пространства.',
    sharedWithMe: 'Доступные мне',
    teams: 'Команды',
    projects: 'Проекты',
    allDrives: 'Все диски',
    pinnedDrives: 'Закреплённые диски',
    pinDrive: 'Закрепить диск',
    unpinDrive: 'Открепить диск',
  },
  vi: {
    drives: 'Ổ đĩa',
    driveNoun: 'Ổ đĩa',
    myDrive: 'Ổ đĩa của tôi',
    workspaceLibrary: 'Ổ đĩa workspace',
    workspaceLibraryCallout:
      'IMAI dùng ổ đĩa này làm nơi mặc định cho sản phẩm, tài liệu tham khảo và tệp được chia sẻ trong toàn bộ workspace.',
    sharedWithMe: 'Được chia sẻ với tôi',
    teams: 'Nhóm',
    projects: 'Dự án',
    allDrives: 'Tất cả drive',
    pinnedDrives: 'Ổ đĩa đã ghim',
    pinDrive: 'Ghim ổ đĩa',
    unpinDrive: 'Bỏ ghim ổ đĩa',
  },
  zh: {
    drives: '云端硬盘',
    driveNoun: '云端硬盘',
    myDrive: '我的云端硬盘',
    workspaceLibrary: '工作区云端硬盘',
    workspaceLibraryCallout:
      'IMAI 默认使用此云端硬盘存放在整个工作区共享的产品、参考资料和文件。',
    sharedWithMe: '与我共享',
    teams: '团队',
    projects: '项目',
    allDrives: '所有云端硬盘',
    pinnedDrives: '已固定的云端硬盘',
    pinDrive: '固定云端硬盘',
    unpinDrive: '取消固定云端硬盘',
  },
  'zh-TW': {
    drives: '雲端硬碟',
    driveNoun: '雲端硬碟',
    myDrive: '我的雲端硬碟',
    workspaceLibrary: '工作區雲端硬碟',
    workspaceLibraryCallout:
      'IMAI 預設使用此雲端硬碟存放在整個工作區共用的產品、參考資料和檔案。',
    sharedWithMe: '與我共用',
    teams: '團隊',
    projects: '專案',
    allDrives: '所有雲端硬碟',
    pinnedDrives: '已固定的雲端硬碟',
    pinDrive: '固定雲端硬碟',
    unpinDrive: '取消固定雲端硬碟',
  },
};

export function getDrivesCopy(locale: string): DrivesCopy {
  const normalized = normalizeLocale(locale);
  return COPY_BY_LOCALE[normalized];
}

const COPY_BY_LOCALE = Object.fromEntries(
  (Object.keys(NAV_COPY) as SupportedLocale[]).map((locale) => [
    locale,
    Object.freeze({
      ...getDriveManagementCopy(locale),
      ...NAV_COPY[locale],
      ...getChannelDriveCopy(locale),
      ...getDriveLocalizedLabels(locale),
      ...getDriveBreakGlassCopy(locale),
    }),
  ])
) as Record<SupportedLocale, DrivesCopy>;
